import { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Palette, Save, Info, Image as ImageIcon, Check } from "lucide-react";

/** Small inline field used across the branding editor.  Kept outside
 *  the parent component so React doesn't rebuild every input on each
 *  keystroke (was causing focus-loss reports). */
function BrandField({ brand, k, l, ph, type = "text", rows = 0, onChange }) {
  return (
    <label className="block" data-testid={`brand-field-${k}`}>
      <span className="text-[10px] uppercase tracking-widest text-[color:var(--aii-muted)] font-bold">{l}</span>
      {rows > 0 ? (
        <textarea value={brand[k] || ""} onChange={e => onChange(k, e.target.value)}
          rows={rows} placeholder={ph}
          className="mt-1 w-full px-3 py-2 border border-[color:var(--aii-border)] rounded-md bg-white text-sm"/>
      ) : (
        <input type={type} value={brand[k] || ""} onChange={e => onChange(k, e.target.value)} placeholder={ph}
          className="mt-1 w-full px-3 py-2 border border-[color:var(--aii-border)] rounded-md bg-white text-sm"/>
      )}
    </label>
  );
}

/**
 * Founder-only Branding editor for the Settings page.
 *
 * Lets the Founder rewrite the platform-wide brand (name, wordmark,
 * tagline, hero copy, colour, logo, footer, per-card kickers/blurbs)
 * that the Login screen consumes.  All fields optional — only the
 * ones the Founder changes get PUT to `/api/brand/config`.
 */
export default function BrandingEditor() {
  const [brand, setBrand] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  // Curated hero-photo gallery for the one-tap login-background picker.
  const [heroPhotos, setHeroPhotos] = useState([]);

  useEffect(() => {
    api.get("/brand/config")
      .then(({ data }) => setBrand(data))
      .catch(() => toast.error("Could not load branding config"));
    api.get("/brand/hero-photos")
      .then(({ data }) => setHeroPhotos(data.photos || []))
      .catch(() => setHeroPhotos([]));
  }, []);

  const set = (k, v) => { setBrand(b => ({ ...b, [k]: v })); setDirty(true); };

  const save = async () => {
    if (!brand) return;
    setSaving(true);
    try {
      const { data } = await api.put("/brand/config", brand);
      setBrand(data.config);
      setDirty(false);
      toast.success("Branding updated — reload to see the new landing page");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  if (!brand) return <div className="p-4 text-sm text-[color:var(--aii-muted)]">Loading branding…</div>;

  return (
    <div className="aii-card p-4 space-y-4" data-testid="branding-editor">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-brand font-bold flex items-center gap-1"><Palette size={11}/> White-label branding</div>
          <h2 className="font-display text-xl font-semibold mt-0.5">Platform brand</h2>
          <p className="text-sm text-[color:var(--aii-text-2)] mt-1 max-w-2xl">
            What every partner + participant sees on the landing page.  Individual partners can override their own logo + name via <b>Partner Admin</b> for a per-URL whitelabel at <code>/p/&lt;slug&gt;</code>.
          </p>
        </div>
        <button data-testid="brand-save" onClick={save} disabled={saving || !dirty}
          className="text-sm px-4 py-2 bg-brand text-white font-semibold rounded-md hover:bg-brand-dark disabled:opacity-50 flex items-center gap-1.5">
          <Save size={13}/> {saving ? "Saving…" : dirty ? "Save changes" : "Saved"}
        </button>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <BrandField brand={brand} onChange={set} k="name" l="Brand name" ph="Aii Group"/>
        <BrandField brand={brand} onChange={set} k="wordmark" l="Wordmark (header caps)" ph="AII GROUP"/>
        <BrandField brand={brand} onChange={set} k="tagline" l="Tagline (under wordmark)" ph="Meaningful Adventures"/>
        <BrandField brand={brand} onChange={set} k="hero_line" l="Hero heading" ph="Sign in to your world"/>
        <BrandField brand={brand} onChange={set} k="hero_accent" l="Hero accent word (highlighted green)" ph="world"/>
        <label className="block sm:col-span-2">
          <span className="text-[10px] uppercase tracking-widest text-[color:var(--aii-muted)] font-bold">Hero subtitle</span>
          <textarea value={brand.hero_subtitle || ""} onChange={e => set("hero_subtitle", e.target.value)} rows={2}
            className="mt-1 w-full px-3 py-2 border border-[color:var(--aii-border)] rounded-md bg-white text-sm"/>
        </label>
        <BrandField brand={brand} onChange={set} k="logo_url" l="Logo URL" ph="/aii-logo.png or https://…"/>
        <BrandField brand={brand} onChange={set} k="favicon_url" l="Favicon URL" ph="/favicon.ico"/>
        <BrandField brand={brand} onChange={set} k="primary_color" l="Primary colour (hex)" ph="#1B7A3E"/>
        <BrandField brand={brand} onChange={set} k="primary_dark" l="Primary dark shade (hex)" ph="#0b3d1f"/>
        <label className="block">
          <span className="text-[10px] uppercase tracking-widest text-[color:var(--aii-muted)] font-bold">Default language</span>
          <select
            data-testid="brand-default-language"
            value={brand.default_language || "en"}
            onChange={e => set("default_language", e.target.value)}
            className="mt-1 w-full px-3 py-2 border border-[color:var(--aii-border)] rounded-md bg-white text-sm">
            <option value="en">🇬🇧 English</option>
            <option value="es">🇪🇸 Español</option>
            <option value="fr">🇫🇷 Français</option>
            <option value="pt">🇵🇹 Português</option>
          </select>
          <span className="text-[10px] text-[color:var(--aii-muted)] mt-1 block">
            Sets the fallback language for the login page. Participants can still switch via the top-right picker.
          </span>
        </label>
        <label className="block sm:col-span-2">
          <span className="text-[10px] uppercase tracking-widest text-[color:var(--aii-muted)] font-bold">Footer text</span>
          <textarea value={brand.footer_text || ""} onChange={e => set("footer_text", e.target.value)} rows={2}
            className="mt-1 w-full px-3 py-2 border border-[color:var(--aii-border)] rounded-md bg-white text-sm"/>
        </label>
        <label className="flex items-center gap-2 sm:col-span-2 text-sm">
          <input type="checkbox" checked={!!brand.hide_vendor_wordmark} onChange={e => set("hide_vendor_wordmark", e.target.checked)}
            data-testid="brand-hide-wordmark"/>
          Hide the ® next to the wordmark (useful when a partner wants a fully neutral landing page)
        </label>
      </div>

      <details className="border border-[color:var(--aii-border)] rounded-md">
        <summary className="cursor-pointer px-3 py-2 text-sm font-semibold hover:bg-slate-50 flex items-center gap-2">
          <ImageIcon size={13}/> Hero background — pick a travel photo for the login page
        </summary>
        <div className="p-3">
          <p className="text-xs text-[color:var(--aii-muted)] mb-2">
            Tap any tile to swap the full-bleed background image on <code>/login</code>.
            The photo is stored as a public URL under <code>brand.hero_bg_url</code> — you can
            also paste a custom URL below if you host your own.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="brand-hero-grid">
            {heroPhotos.map(p => {
              const active = brand.hero_bg_url === p.full;
              return (
                <button key={p.id}
                        data-testid={`brand-hero-${p.id}`}
                        type="button"
                        onClick={() => set("hero_bg_url", p.full)}
                        title={p.label}
                        className={`relative aspect-video rounded-md overflow-hidden border-2 ${active ? "border-brand ring-2 ring-brand/30" : "border-transparent hover:border-slate-300"}`}>
                  <img src={p.thumb} alt={p.label} loading="lazy" className="w-full h-full object-cover"/>
                  {active && (
                    <div className="absolute inset-0 bg-brand/40 flex items-center justify-center">
                      <div className="bg-white rounded-full p-1"><Check size={14} className="text-brand"/></div>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
          <BrandField brand={brand} onChange={set} k="hero_bg_url" l="Custom hero URL (overrides the picker)"
                     ph="https://images.example.com/hero.jpg"/>
        </div>
      </details>

      <details className="border border-[color:var(--aii-border)] rounded-md">
        <summary className="cursor-pointer px-3 py-2 text-sm font-semibold hover:bg-slate-50">Advanced — landing card copy (kickers, blurbs, footnotes)</summary>
        <div className="p-3 grid sm:grid-cols-2 gap-3">
          <BrandField brand={brand} onChange={set} k="team_card_kicker" l="Team card kicker" ph="For Team"/>
          <BrandField brand={brand} onChange={set} k="team_card_title" l="Team card title" ph="Team sign in"/>
          <label className="block sm:col-span-2">
            <span className="text-[10px] uppercase tracking-widest text-[color:var(--aii-muted)] font-bold">Team card blurb</span>
            <textarea value={brand.team_card_blurb || ""} onChange={e => set("team_card_blurb", e.target.value)} rows={2}
              className="mt-1 w-full px-3 py-2 border border-[color:var(--aii-border)] rounded-md bg-white text-sm"/>
          </label>
          <BrandField brand={brand} onChange={set} k="participant_card_kicker" l="Participant card kicker" ph="For Participants"/>
          <BrandField brand={brand} onChange={set} k="partner_card_kicker" l="Partner card kicker" ph="For Sending Partners"/>
          <label className="block sm:col-span-2">
            <span className="text-[10px] uppercase tracking-widest text-[color:var(--aii-muted)] font-bold">Participant card blurb</span>
            <textarea value={brand.participant_card_blurb || ""} onChange={e => set("participant_card_blurb", e.target.value)} rows={2}
              className="mt-1 w-full px-3 py-2 border border-[color:var(--aii-border)] rounded-md bg-white text-sm"/>
          </label>
          <label className="block sm:col-span-2">
            <span className="text-[10px] uppercase tracking-widest text-[color:var(--aii-muted)] font-bold">Participant card footnote</span>
            <input value={brand.participant_card_footnote || ""} onChange={e => set("participant_card_footnote", e.target.value)}
              className="mt-1 w-full px-3 py-2 border border-[color:var(--aii-border)] rounded-md bg-white text-sm"/>
          </label>
          <label className="block sm:col-span-2">
            <span className="text-[10px] uppercase tracking-widest text-[color:var(--aii-muted)] font-bold">Partner card blurb</span>
            <textarea value={brand.partner_card_blurb || ""} onChange={e => set("partner_card_blurb", e.target.value)} rows={2}
              className="mt-1 w-full px-3 py-2 border border-[color:var(--aii-border)] rounded-md bg-white text-sm"/>
          </label>
          <label className="block sm:col-span-2">
            <span className="text-[10px] uppercase tracking-widest text-[color:var(--aii-muted)] font-bold">Partner card footnote</span>
            <input value={brand.partner_card_footnote || ""} onChange={e => set("partner_card_footnote", e.target.value)}
              className="mt-1 w-full px-3 py-2 border border-[color:var(--aii-border)] rounded-md bg-white text-sm"/>
          </label>
        </div>
      </details>

      <div className="flex items-start gap-2 bg-sky-50 border border-sky-200 rounded p-3 text-xs text-sky-900">
        <Info size={13} className="mt-0.5 shrink-0"/>
        <div>
          After saving, hit the <b>/login</b> page in a fresh tab (or reload) to see the new copy.  Team members inside the app keep the Aii Ops sidebar branding — this only rebrands the login / landing screen and the browser tab title.
        </div>
      </div>
    </div>
  );
}
